import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECTS = ["General Enquiry", "Wholesale & Retail", "Press", "Product Question"];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "General Enquiry", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. We'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "General Enquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your details and try again." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-md border border-gray-300 px-4 py-3 text-ink placeholder:text-gray-400 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10 transition-colors duration-200";

  return (
    <main data-testid="contact-page" className="pt-[110px] bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-3 inline-block border-l-2 border-gold pl-3">Get in touch</p>
          <h1 className="font-display text-4xl lg:text-5xl font-medium tracking-tight text-ink">Let's connect</h1>
          <p className="mt-5 text-lg text-gray-600 max-w-md leading-relaxed">
            Whether you're a retailer, partner, or simply curious about our formulas, we'd love to hear from you as we launch Femora Life to the world.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <span className="h-10 w-10 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center"><Mail size={18} className="text-rose" /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400">Email</p>
                <a href="mailto:hello@femora.life" className="font-semibold text-ink hover:text-rose transition-colors duration-200">hello@femora.life</a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="h-10 w-10 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center"><Phone size={18} className="text-rose" /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400">Phone</p>
                <a href="tel:+918019369369" className="font-semibold text-ink hover:text-rose transition-colors duration-200">+91 80193 69369</a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="h-10 w-10 rounded-md bg-gray-50 border border-gray-200 flex items-center justify-center"><Globe size={18} className="text-rose" /></span>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-400">Reach</p>
                <p className="font-semibold text-ink">Available globally · 2025</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="bg-gray-50 border border-gray-200 rounded-xl p-6 md:p-8 space-y-5">
          <div>
            <label className="text-sm font-medium text-ink block mb-1.5">Your name</label>
            <input required value={form.name} onChange={update("name")} data-testid="contact-name-input" placeholder="Jane Doe" className={inputCls} />
          </div>
          <div>
            <label className="text-sm font-medium text-ink block mb-1.5">Email address</label>
            <input required type="email" value={form.email} onChange={update("email")} data-testid="contact-email-input" placeholder="jane@email.com" className={inputCls} />
          </div>
          <div>
            <label className="text-sm font-medium text-ink block mb-2">Subject</label>
            <div className="flex flex-wrap gap-2">
              {SUBJECTS.map((s) => (
                <button type="button" key={s} onClick={() => setForm((f) => ({ ...f, subject: s }))}
                  data-testid={`contact-subject-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`text-sm font-medium rounded-md px-4 py-2 border transition-colors duration-200 ${form.subject === s ? "bg-ink text-white border-ink" : "bg-white border-gray-300 text-gray-600 hover:border-ink"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-ink block mb-1.5">Message</label>
            <textarea required rows={4} value={form.message} onChange={update("message")} data-testid="contact-message-input" placeholder="Tell us how we can help…" className={`${inputCls} resize-none`} />
          </div>
          <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-8 py-3.5 hover:bg-rose transition-colors duration-200 disabled:opacity-50">
            {loading ? "Sending…" : "Send message"} <ArrowRight size={16} />
          </button>
        </form>
      </div>
    </main>
  );
};

export default Contact;
